import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Item } from "@egalregal/shared";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";
import { placementFailureNotice } from "../items/placementNotice";
import { placementKey, type PlacementOutcome } from "../items/placements";
import { usePlacementsStore } from "../items/placementsStore";
import type { PlacementOption } from "./placements";

/** One successful immediate write; the surface's one-level undo target. */
export interface QuantityWrite {
  containerId: string;
  itemId: string;
}

interface ScanItemPanelProps {
  item: Item;
  placements: PlacementOption[];
  /** The surface-held undo target for this item, or null when there is none. */
  undo: QuantityWrite | null;
  /** Reports a successful write, so the surface can offer one-level undo. */
  onWritten: (write: QuantityWrite) => void;
  /** Runs the pending undo; resolves the write outcome, or null with no target. */
  onUndo: () => Promise<PlacementOutcome | null>;
}

/**
 * Placement and quantity for a known item on the global `/scan` view
 * (results-03). The block is identical for one placement and several: the
 * full container breadcrumb is always shown — as one navigable row, or as a
 * selector whose labels link to their containers — and the `+`/`−` stepper
 * always writes the selected placement's absolute total immediately through the
 * placement ledger (placement-ledger 03; no confirm button anywhere in the
 * global scanner). Choosing a placement is state only; it never writes. The
 * in-flight/confirmed total comes from the slice's optimistic map, so a failed
 * write falls back to the stored quantity. A one-level undo lives next to the
 * stepper, and an unplaced item gets neither editor nor container controls.
 */
export function ScanItemPanel({
  item,
  placements,
  undo,
  onWritten,
  onUndo,
}: ScanItemPanelProps) {
  const t = useT();
  const optimistic = usePlacementsStore((state) => state.optimistic);
  const setTotal = usePlacementsStore((state) => state.setTotal);
  const [selectedId, setSelectedId] = useState(() => placements[0]?.containerId ?? "");
  // The last totals this panel saw confirmed, so a failed undo (which drops the
  // slice's optimistic key) falls back to the write it was reversing rather
  // than to the placements snapshot captured when the code resolved.
  const [confirmed, setConfirmed] = useState<Record<string, number>>(() =>
    Object.fromEntries(placements.map((placement) => [placement.containerId, placement.quantity])),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No placement means nothing to count here: placing stays the container-add
  // flow's job, and the CTA bar offers "View item".
  if (placements.length === 0) return null;

  const multi = placements.length > 1;
  const selected = placements.find((p) => p.containerId === selectedId) ?? placements[0]!;
  const totalOf = (placement: PlacementOption) =>
    optimistic[placementKey(placement.containerId, item.id)] ??
    confirmed[placement.containerId] ??
    placement.quantity;
  const quantity = totalOf(selected);

  const select = (placement: PlacementOption) => {
    // Choosing a placement must never write — only a stepper tap does.
    setSelectedId(placement.containerId);
    setError(null);
  };

  const step = async (delta: number) => {
    if (saving) return;
    const next = Math.max(1, quantity + delta);
    // Floored at 1: a scan never unplaces an item, so there is nothing to write.
    if (next === quantity) return;
    setSaving(true);
    setError(null);
    const outcome = await setTotal(selected.containerId, item, next);
    setSaving(false);
    if (outcome.status !== "saved") {
      // A failed write is never queued (ADR-0005): the ledger drops the
      // optimistic total, so the stored total stays the truth and the notice
      // says why the change was not saved.
      log.warn(
        { outcome, itemId: item.id, containerId: selected.containerId },
        "Failed to adjust scanned item quantity",
      );
      setError(placementFailureNotice(t, outcome));
      return;
    }
    setConfirmed((current) => ({ ...current, [selected.containerId]: outcome.quantity }));
    onWritten({ containerId: selected.containerId, itemId: item.id });
  };

  const undoWrite = async () => {
    if (!undo || saving) return;
    const containerId = undo.containerId;
    setSaving(true);
    setError(null);
    const outcome = await onUndo();
    setSaving(false);
    if (!outcome) return;
    if (outcome.status !== "saved") {
      // Keep the displayed total and the pending undo; surface the error.
      setError(placementFailureNotice(t, outcome));
      return;
    }
    setConfirmed((current) => ({ ...current, [containerId]: outcome.quantity }));
  };

  return (
    <Box data-testid="scan-item-quantity" sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {multi
          ? t("searchInContainers", { count: String(placements.length) })
          : t("scanAdjustContainer")}
      </Typography>

      {multi ? (
        <RadioGroup
          aria-label={t("scanAdjustChoosePlacement")}
          value={selected.containerId}
          onChange={(event) => {
            const placement = placements.find((p) => p.containerId === event.target.value);
            if (placement) select(placement);
          }}
        >
          {placements.map((placement) => (
            <Stack
              key={placement.containerId}
              direction="row"
              alignItems="center"
              spacing={0.5}
            >
              <Radio
                size="small"
                value={placement.containerId}
                inputProps={{ "aria-label": placement.path }}
              />
              <PlacementLink placement={placement} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: "nowrap" }}
                data-testid={`scan-placement-quantity-${placement.containerId}`}
              >
                × {totalOf(placement)}
              </Typography>
            </Stack>
          ))}
        </RadioGroup>
      ) : (
        <PlacementLink placement={selected} testId="scan-placement-link" />
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        <Button
          aria-label={t("decrementQuantity")}
          onClick={() => void step(-1)}
          disabled={saving || quantity <= 1}
          sx={{ minWidth: 40 }}
        >
          −
        </Button>
        <Typography
          data-testid="scan-adjust-quantity"
          variant="h6"
          sx={{ minWidth: 32, textAlign: "center" }}
        >
          {quantity}
        </Typography>
        <Button
          aria-label={t("incrementQuantity")}
          onClick={() => void step(1)}
          disabled={saving}
          sx={{ minWidth: 40 }}
        >
          +
        </Button>
        {undo && (
          <Button
            size="small"
            onClick={() => void undoWrite()}
            disabled={saving}
            data-testid="scan-undo"
          >
            {t("undo")}
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {t("scanAdjustImmediate")}
      </Typography>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The container breadcrumb as the one navigable placement affordance, shared by
 * the single row and each selector entry. It opens the container through the
 * shared container-open action with the scan origin (results-03).
 */
function PlacementLink({
  placement,
  testId,
}: {
  placement: PlacementOption;
  testId?: string;
}) {
  const t = useT();
  const openContainer = useOpenContainer();
  return (
    <Button
      size="small"
      onClick={() => void openContainer(placement.containerId, { from: "scan" })}
      aria-label={t("scanOpenContainer", { name: placement.path })}
      data-testid={testId}
      sx={{
        minWidth: 0,
        px: 0.5,
        justifyContent: "flex-start",
        textAlign: "left",
        textTransform: "none",
      }}
    >
      {placement.path}
    </Button>
  );
}
